// The list of saved matches, and the bar that plays one back.
//
// Two small pieces of UI that only make sense together: one picks a recording,
// the other watches it. Both are deliberately thin -- the list is a query
// against the server, which did the work of describing each match by replaying
// it, and the bar is four buttons over `ReplayMatch.seek`.

import type { Profiles, ReplayFilter, ReplayMeta } from './profile.ts';
import type { Replay } from './log.ts';
import type { ReplayMatch } from './watch.ts';

const el = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

/** "3 minutes ago", "yesterday", "12 Mar" -- whichever is the shortest true one. */
export function whenText(at: number, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * How one row reads.
 *
 * The opponent and the two legends, because that is what a player recognises a
 * match by. A date identifies nothing once there are fifty of them, so it is
 * the quiet half of the row rather than its title.
 */
export function rowText(meta: ReplayMeta): { who: string; sub: string } {
  const versus = meta.opponent === 'human'
    ? (meta.versus ?? 'another player')
    : `the AI${meta.versus ? ` (${meta.versus})` : ''}`;
  const mine = meta.legend ?? 'an unknown deck';
  const theirs = meta.legendVs ?? 'an unknown deck';
  return {
    who: `${mine} vs ${theirs}`,
    sub: `${versus} · ${meta.turns ?? '?'} turns · ${meta.moves} moves`,
  };
}

/** The link that shares a recording, which is the page with its id on it. */
export function shareLink(id: string, origin = location.origin): string {
  return `${origin}/?show=true&replay=${encodeURIComponent(id)}`;
}

export interface BrowserOptions {
  profiles: Profiles;
  /** Called with a chosen recording; the panel closes itself first. */
  onWatch: (replay: Replay, meta: ReplayMeta) => void;
}

export class ReplayBrowser {
  private readonly opts: BrowserOptions;
  private readonly panel = el('replays');
  private readonly list = el('replays-list');
  private readonly empty = el('replays-empty');
  private readonly search = el<HTMLInputElement>('replays-search');
  private readonly opponent = el<HTMLSelectElement>('replays-opponent');
  private readonly outcome = el<HTMLSelectElement>('replays-outcome');
  private readonly legend = el<HTMLSelectElement>('replays-legend');
  private readonly against = el<HTMLSelectElement>('replays-against');
  private searchTimer: number | null = null;

  constructor(opts: BrowserOptions) {
    this.opts = opts;
    el('replays-close').addEventListener('click', () => this.close());
    for (const control of [this.opponent, this.outcome, this.legend, this.against]) {
      control.addEventListener('change', () => void this.refresh());
    }
    // Typed one letter at a time, so the query waits for the typing to stop
    // rather than asking the server about every keystroke.
    this.search.addEventListener('input', () => {
      if (this.searchTimer !== null) clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => void this.refresh(), 220);
    });
  }

  async open(): Promise<void> {
    this.panel.hidden = false;
    await this.refresh();
  }

  close(): void {
    this.panel.hidden = true;
  }

  private filter(): ReplayFilter {
    const filter: ReplayFilter = {};
    if (this.opponent.value) filter.opponent = this.opponent.value as 'ai' | 'human';
    if (this.outcome.value) filter.outcome = this.outcome.value as 'win' | 'loss' | 'draw';
    if (this.legend.value) filter.legend = this.legend.value;
    if (this.against.value) filter.legendVs = this.against.value;
    const text = this.search.value.trim();
    if (text) filter.q = text;
    return filter;
  }

  private async refresh(): Promise<void> {
    const page = await this.opts.profiles.replays(this.filter());

    // The legends worth offering are the ones this player has actually
    // played, and the server says which those are -- so the picker can never
    // offer a deck with nothing behind it. Rebuilt only when the set changes,
    // or choosing one would reset the control the moment it was used.
    this.fillLegends(this.legend, page.legends, 'Any deck of mine');
    this.fillLegends(this.against, page.against, 'Any deck of theirs');

    this.list.replaceChildren();
    for (const meta of page.replays) this.list.append(this.row(meta));

    const filtered = Object.keys(this.filter()).length > 0;
    this.empty.hidden = page.replays.length > 0;
    this.empty.textContent = filtered
      ? 'No saved matches match that.'
      : 'Nothing saved yet. Finished games are kept here automatically.';
  }

  private fillLegends(control: HTMLSelectElement, values: string[], anyLabel: string): void {
    const wanted = [anyLabel, ...values].join(' ');
    if (control.dataset.filled === wanted) return;
    control.dataset.filled = wanted;

    const chosen = control.value;
    control.replaceChildren();
    const any = document.createElement('option');
    any.value = '';
    any.textContent = anyLabel;
    control.append(any);
    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      control.append(option);
    }
    // A legend that has fallen off the list cannot stay selected, or the
    // filter would keep hiding everything with no visible reason.
    control.value = values.includes(chosen) ? chosen : '';
  }

  private row(meta: ReplayMeta): HTMLElement {
    const row = document.createElement('div');
    row.className = 'replay-row';

    const outcome = document.createElement('div');
    outcome.className = `replay-outcome ${meta.outcome ?? 'none'}`;
    outcome.textContent = meta.outcome ? meta.outcome.toUpperCase() : '—';

    const { who, sub } = rowText(meta);
    const body = document.createElement('div');
    const title = document.createElement('div');
    title.className = 'replay-who';
    title.textContent = who;
    const detail = document.createElement('div');
    detail.className = 'replay-sub';
    detail.textContent = sub;
    body.append(title, detail);

    const when = document.createElement('div');
    when.className = 'replay-when';
    when.textContent = whenText(meta.created);

    const acts = document.createElement('div');
    acts.className = 'replay-acts';

    const share = document.createElement('button');
    share.type = 'button';
    share.textContent = meta.shared ? 'Shared' : 'Share';
    share.classList.toggle('on', meta.shared);
    share.title = meta.shared
      ? 'Anyone with the link can watch this. Click to stop sharing.'
      : 'Make a link anyone can watch';
    share.addEventListener('click', (e) => {
      e.stopPropagation();
      void this.toggleShare(meta, share);
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.textContent = 'Delete';
    remove.addEventListener('click', (e) => {
      e.stopPropagation();
      void this.remove(meta, row);
    });

    acts.append(share, remove);
    row.append(outcome, body, when, acts);
    row.addEventListener('click', () => void this.watch(meta));
    return row;
  }

  private async toggleShare(meta: ReplayMeta, button: HTMLButtonElement): Promise<void> {
    const now = await this.opts.profiles.shareReplay(meta.id, !meta.shared);
    if (now === null) return;
    meta.shared = now;
    button.textContent = now ? 'Shared' : 'Share';
    button.classList.toggle('on', now);
    if (!now) return;
    // Sharing without handing over the link is half a feature, and the
    // clipboard is the only place a link is any use.
    const link = shareLink(meta.id);
    try {
      await navigator.clipboard.writeText(link);
      button.textContent = 'Link copied';
      setTimeout(() => { button.textContent = 'Shared'; }, 1600);
    } catch {
      // A browser that refuses the clipboard is not a failure to share.
      window.prompt('Share this link', link);
    }
  }

  private async remove(meta: ReplayMeta, row: HTMLElement): Promise<void> {
    if (!window.confirm('Delete this saved match?')) return;
    if (await this.opts.profiles.deleteReplay(meta.id)) row.remove();
  }

  private async watch(meta: ReplayMeta): Promise<void> {
    const got = await this.opts.profiles.replay(meta.id);
    if (!got) return;
    this.close();
    this.opts.onWatch(got.replay, got.meta);
  }
}

/**
 * The transport for a recording: play, pause, step, and a scrubber.
 *
 * Everything here is `seek`, which rebuilds the position from the seed. That
 * is why stepping backwards costs the same as stepping forwards and why there
 * is no separate "undo": there is nothing to undo, only a different number of
 * moves to have applied.
 */
export class WatchBar {
  private readonly bar = el('watchbar');
  private readonly seek = el<HTMLInputElement>('watch-seek');
  private readonly count = el('watch-count');
  private readonly play = el<HTMLButtonElement>('watch-play');
  private readonly title = el('watch-title');
  private readonly match: ReplayMatch;
  private timer: number | null = null;
  /** Milliseconds a move is left on screen while playing. */
  private pace = 900;

  constructor(match: ReplayMatch, meta: ReplayMeta | null, onExit: () => void) {
    this.match = match;
    this.title.textContent = meta ? rowText(meta).who : '';
    this.seek.max = String(match.length);
    this.seek.value = '0';

    el('watch-start').addEventListener('click', () => { this.pause(); this.to(0); });
    el('watch-back').addEventListener('click', () => { this.pause(); this.to(match.position - 1); });
    el('watch-fwd').addEventListener('click', () => { this.pause(); this.to(match.position + 1); });
    el('watch-end').addEventListener('click', () => { this.pause(); this.to(match.length); });
    this.play.addEventListener('click', () => this.toggle());
    this.seek.addEventListener('input', () => { this.pause(); this.to(Number(this.seek.value)); });
    el('watch-exit').addEventListener('click', () => { this.pause(); this.hide(); onExit(); });

    // Follow the match rather than only this bar's own buttons. Anything else
    // that moves the position -- a keyboard shortcut, the console, a future
    // click on the log to jump to a move -- would otherwise leave the scrubber
    // and the counter describing a position the board is no longer at.
    const previous = match.onChange;
    match.onChange = () => { previous?.(); this.update(); };

    this.bar.hidden = false;
    this.update();
  }

  hide(): void {
    this.pause();
    this.bar.hidden = true;
  }

  private toggle(): void {
    if (this.timer !== null) { this.pause(); return; }
    // Pressing play at the end starts again, rather than doing nothing.
    if (this.match.atEnd) this.to(0);
    this.timer = window.setInterval(() => {
      if (!this.match.step()) { this.pause(); return; }
      this.update();
    }, this.pace);
    this.play.textContent = '⏸';
    this.play.title = 'Pause';
  }

  private pause(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.play.textContent = '▶';
    this.play.title = 'Play';
  }

  private to(n: number): void {
    this.match.seek(n);
    this.update();
  }

  private update(): void {
    this.seek.value = String(this.match.position);
    this.count.textContent = `${this.match.position} / ${this.match.length}`;
  }
}
